// The interior tutorial inside the practice room: per step, what to point at and
// when the player has done it. Completion is read from the game itself (build
// mode, the room's furniture, the scene), never from taps on the overlay.
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as PIXI from 'pixi.js';
import type { Viewport } from 'pixi-viewport';
import type { HouseFurniture } from '../../../models/apiTypes';
import {
  INTERIOR_TUTORIAL_STEPS,
  inTarget,
  TUTORIAL_ITEM,
  TUTORIAL_TARGETS,
  type TutorialItemStep,
  type TutorialTarget,
} from '../../../domain/interiorTutorial';
import { HALF_TILE_HEIGHT, HALF_TILE_WIDTH } from '../../../domain/grid';
import { CONFLICT_TINT } from '../../../domain/placementTints';
import type { RoomSceneState } from '../state/roomSceneState';
import { impactOccurred, notificationOccurred } from '../../../utils/telegram/hapticFeedback';
import { playSfx } from '../../../utils/sfx';
import CelebrationConfetti from '../../../components/CelebrationConfetti';
import TutorialSpotlight, { ELDER_SRC, TutorialGuideCard, type ScreenRect } from './TutorialSpotlight';
import { useInteriorTutorial } from './useInteriorTutorial';

interface RoomTutorialProps {
  mapObjectId: number;
  buildMode: boolean;
  houseFurniture: HouseFurniture[];
  sceneRef: { current: RoomSceneState };
  getRoomContainer: () => PIXI.Container | null;
  getViewport: () => Viewport | null;
}

const TILE_FILL = 0x39ff14;
/** Room-local y of the floor's centre, and the room-local width the overview fits on screen. */
const ROOM_CENTER_Y = 230;
const OVERVIEW_WIDTH = 1080;
/** How long the guide says "Nice!" after a step before the next instruction. */
const PRAISE_MS = 1100;
/** The steps the progress dots count (the finish card is the reward, not a step). */
const COUNTED_STEPS = INTERIOR_TUTORIAL_STEPS.length - 1;

interface Geometry {
  from: ScreenRect | null;
  to: { x: number; y: number } | null;
}
const NO_GEOMETRY: Geometry = { from: null, to: null };

const near = (a: number, b: number) => Math.abs(a - b) < 0.5;
function sameGeometry(prev: Geometry, from: ScreenRect | null, to: { x: number; y: number } | null): boolean {
  const sameFrom = prev.from === from || (!!prev.from && !!from && near(prev.from.x, from.x) && near(prev.from.y, from.y)
    && near(prev.from.width, from.width) && near(prev.from.height, from.height));
  const sameTo = prev.to === to || (!!prev.to && !!to && near(prev.to.x, to.x) && near(prev.to.y, to.y));
  return sameFrom && sameTo;
}

/** Anchor tile of a floor item from its stored floor position. */
const anchorTile = (x: number, y: number) => ({ x: (x / HALF_TILE_WIDTH + y / HALF_TILE_HEIGHT) / 2, y: (y / HALF_TILE_HEIGHT - x / HALF_TILE_WIDTH) / 2 });
/** Room-local point of a (fractional) tile coordinate. */
const tilePoint = (tx: number, ty: number) => ({ x: (tx - ty) * HALF_TILE_WIDTH, y: (tx + ty) * HALF_TILE_HEIGHT });

/** Screen point of a room-local point (the room canvas fills the screen from 0,0). */
function screenOf(room: PIXI.Container, x: number, y: number): { x: number; y: number } {
  const p = room.toGlobal({ x, y });
  return { x: p.x, y: p.y };
}

function boundsOf(marker: PIXI.Container | undefined): ScreenRect | null {
  if (!marker || marker.destroyed) return null;
  const b = marker.getBounds();
  return { x: b.x, y: b.y, width: b.width, height: b.height };
}

/** Draw the target footprint (a pulsing green diamond) on the floor. */
function drawTarget(g: PIXI.Graphics, target: TutorialTarget, alpha: number): void {
  const a = tilePoint(target.tileX - target.w - 0.5, target.tileY - target.h - 0.5);
  const b = tilePoint(target.tileX - 0.5, target.tileY - target.h - 0.5);
  const c = tilePoint(target.tileX - 0.5, target.tileY - 0.5);
  const d = tilePoint(target.tileX - target.w - 0.5, target.tileY - 0.5);
  g.clear();
  g.poly([a.x, a.y, b.x, b.y, c.x, c.y, d.x, d.y]).fill({ color: TILE_FILL, alpha: 0.18 + 0.17 * alpha }).stroke({ color: TILE_FILL, width: 2, alpha: 0.9 });
}

export default function RoomTutorial({ mapObjectId, buildMode, houseFurniture, sceneRef, getRoomContainer, getViewport }: RoomTutorialProps) {
  const { t } = useTranslation();
  const { active, step, phase, advance, skip, finish } = useInteriorTutorial(mapObjectId);
  const stepKey = `${step}:${phase}:${buildMode}`;
  const [geo, setGeo] = useState<Geometry>(NO_GEOMETRY);
  const [radialOpen, setRadialOpen] = useState(false);
  const flipStartRef = useRef<boolean | null>(null);

  // A step just done: confetti, a chime, and "Nice!" for a moment.
  const [seenStep, setSeenStep] = useState(step);
  const [praiseKey, setPraiseKey] = useState(0);
  const [praiseOver, setPraiseOver] = useState(0);
  if (step !== seenStep) {
    setSeenStep(step);
    if (seenStep && step) setPraiseKey((k) => k + 1);
  }
  const praising = praiseKey > 0 && praiseOver !== praiseKey;
  useEffect(() => {
    if (!praiseKey) return;
    playSfx('step');
    notificationOccurred('success');
    const id = window.setTimeout(() => setPraiseOver(praiseKey), PRAISE_MS);
    return () => window.clearTimeout(id);
  }, [praiseKey]);

  const itemOf = (catalogId: number) => houseFurniture.find((f) => f.furniture_catalog_id === catalogId && f.is_placed);
  const bed = itemOf(TUTORIAL_ITEM.bed);
  const table = itemOf(TUTORIAL_ITEM.table);
  const bouquet = itemOf(TUTORIAL_ITEM.bouquet);
  const target = step === 'bed' && phase === 'place' ? TUTORIAL_TARGETS.bed
    : step === 'table' && phase === 'place' ? TUTORIAL_TARGETS.table : null;
  const moving = step === 'bed' ? bed : step === 'table' ? table : step === 'bouquet' ? bouquet : undefined;
  const pointAt = step === 'flip' ? bed : moving;

  // Follow the camera: where the item, the target and the table are on screen.
  useEffect(() => {
    if (!active) return;
    let raf = 0;
    const tick = () => {
      const markers = sceneRef.current.furniture.markers;
      const room = getRoomContainer();
      const from = pointAt ? boundsOf(markers.get(pointAt.id)) : null;
      let to: { x: number; y: number } | null = null;
      if (room && target) {
        const p = tilePoint(target.tileX - target.w / 2 - 0.5, target.tileY - target.h / 2 - 0.5);
        to = screenOf(room, p.x, p.y);
      } else if (step === 'bouquet' && table) {
        const tb = boundsOf(markers.get(table.id));
        to = tb ? { x: tb.x + tb.width / 2, y: tb.y + tb.height * 0.35 } : null;
      }
      setGeo((prev) => (sameGeometry(prev, from, to) ? prev : { from, to }));
      setRadialOpen(!!document.querySelector('[data-tutorial="radial-mirror"]'));
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [active, step, pointAt, target, table, sceneRef, getRoomContainer]);

  // ── Completion: read from the game ──
  useEffect(() => {
    if (!active || !step) return;
    const markers = sceneRef.current.furniture.markers;
    const isRed = (item: HouseFurniture) => (markers.get(item.id)?.children[0] as PIXI.Sprite | undefined)?.tint === CONFLICT_TINT;
    const landed = (item: HouseFurniture | undefined, at: TutorialTarget) =>
      !!item && item.x != null && item.y != null && inTarget(anchorTile(item.x, item.y), at) && !isRed(item);
    let done = false;
    if (step === 'build') done = buildMode;
    else if (step === 'walls' || step === 'floor') done = phase === 'place';
    else if (step === 'bed') done = phase === 'place' && landed(bed, TUTORIAL_TARGETS.bed);
    else if (step === 'table') done = phase === 'place' && landed(table, TUTORIAL_TARGETS.table);
    else if (step === 'bouquet') done = phase === 'place' && bouquet?.placement_mode === 'surface';
    else if (step === 'flip') {
      if (flipStartRef.current === null && bed) flipStartRef.current = !!bed.is_mirrored;
      done = !!bed && flipStartRef.current !== null && !!bed.is_mirrored !== flipStartRef.current;
    }
    if (done) {
      if (step !== 'build') impactOccurred('medium');
      flipStartRef.current = null;
      advance();
    }
  }, [active, step, phase, buildMode, bed, table, bouquet, advance, sceneRef, geo]);

  // Placing / flipping: show the whole room, so the item and its spot are both on
  // screen (a bought item gets the camera's focus). After that zoom ends.
  const wantsOverview = step === 'flip' || phase === 'place';
  useEffect(() => {
    if (!active || !wantsOverview) return;
    const id = window.setTimeout(() => {
      const vp = getViewport();
      const room = getRoomContainer();
      if (!vp || !room) return;
      vp.animate({
        position: { x: room.position.x, y: room.position.y + ROOM_CENTER_Y },
        scale: Math.min(1, vp.screenWidth / OVERVIEW_WIDTH),
        time: 600,
        ease: 'easeInOutSine',
        removeOnInterrupt: true,
      });
    }, 800);
    return () => window.clearTimeout(id);
  }, [active, wantsOverview, step, getViewport, getRoomContainer]);

  // ── Target tiles on the floor (bed / table placing) ──
  useEffect(() => {
    const room = getRoomContainer();
    if (!room || !target || !buildMode) return;
    const g = new PIXI.Graphics();
    g.zIndex = 5; // on the floor, under the furniture
    g.eventMode = 'none';
    room.addChild(g);
    let raf = 0;
    const pulse = () => { drawTarget(g, target, (Math.sin(performance.now() / 300) + 1) / 2); raf = requestAnimationFrame(pulse); };
    raf = requestAnimationFrame(pulse);
    return () => { cancelAnimationFrame(raf); g.destroy(); };
  }, [target, buildMode, getRoomContainer]);

  if (!active || !step) return null;

  if (step === 'finish') {
    return (
      <>
        <CelebrationConfetti pieces={260} sound="victory" />
        <div className="tutorial-spotlight">
          <div className="tutorial-finish-wrap">
            <div className="tutorial-finish" role="dialog" aria-modal="true">
              <img className="tutorial-finish__elder" src={ELDER_SRC} alt="" />
              <p className="tutorial-finish__title">{t("Your room is ready!")}</p>
              <p className="tutorial-finish__text">{t("Now make your real home cosy")}</p>
              <button type="button" className="tutorial-finish__go" onClick={finish}>{t("Go to my house")}</button>
            </div>
          </div>
        </div>
      </>
    );
  }

  const progress = { done: INTERIOR_TUTORIAL_STEPS.indexOf(step), total: COUNTED_STEPS };
  const common = { onSkip: skip, onSkipStep: advance, stepKey, progress, vignette: true };
  const shopCaption: Record<TutorialItemStep, string> = {
    walls: t("First, new walls. Open the shop"),
    floor: t("Now the floor. Open the shop"),
    bed: t("Time for a bed. Open the shop"),
    table: t("A table by the bed. Open the shop"),
    bouquet: t("Flowers for the table. Open the shop"),
  };

  const view = (() => {
    // Everything below needs build mode; if it went off, ask for it again.
    if (step === 'build' || !buildMode) {
      return <TutorialSpotlight {...common} target="build-toggle" gesture="tap" block caption={t("Turn on building")} />;
    }
    if (step === 'flip') {
      if (radialOpen) return <TutorialSpotlight {...common} target="radial-mirror" gesture="tap" caption={t("Tap Flip")} />;
      return <TutorialSpotlight {...common} rect={geo.from} gesture="tap" caption={t("Tap the bed")} />;
    }
    if (phase === 'buy') return <TutorialSpotlight {...common} target="shop-button" gesture="tap" block caption={shopCaption[step]} />;
    // Place: carry the item to the glowing spot (or onto the table).
    return (
      <TutorialSpotlight
        {...common}
        rect={geo.from}
        gesture="drag"
        dragTo={geo.to}
        caption={step === 'bouquet' ? t("Put it on the table") : t("Drag it here")}
      />
    );
  })();

  if (praising) {
    return (
      <>
        <CelebrationConfetti key={praiseKey} pieces={70} sound={null} gravity={0.35} />
        <div className="tutorial-spotlight">
          <div className="tutorial-guide tutorial-guide--praise">
            <TutorialGuideCard progress={progress}>{t("Nice!")}</TutorialGuideCard>
          </div>
        </div>
      </>
    );
  }
  return view;
}
